/**
 * useOutlineSlideStatus - Hook for tracking slide generation status
 *
 * Polls the outline to get latest slide generation statuses and
 * populates the slideConversations in the video workspace store.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useVideoWorkspaceStore, SlideConversation, SlideGenerationStatus } from '@/stores/videoWorkspaceStore';
import { outlineAPI } from '@/services/api';
import { mergeServerOutlineIntoLocal } from '@/utils/outlineMerge';

interface UseOutlineSlideStatusOptions {
  /** Poll interval in milliseconds (default: 3000ms) */
  pollInterval?: number;
  /** Whether polling is enabled (default: true when outline exists) */
  enabled?: boolean;
}

interface SlideStatusInfo {
  outlineSlideId: string;
  status: SlideGenerationStatus;
  slideConversationId?: string;
  generatingThinking?: string;
  errorMessage?: string;
}

export function useOutlineSlideStatus(
  conversationId: string,
  options: UseOutlineSlideStatusOptions = {}
) {
  const { pollInterval = 3000, enabled = true } = options;

  const {
    setOutline,
    slideConversations,
    setSlideConversations,
  } = useVideoWorkspaceStore();

  // Read outline reactively only for derived state (hasGeneratingSlides, getSlideStatus, etc.)
  // but NOT for the fetchSlideStatuses callback - that reads from getState() to avoid
  // creating a cascade where optimistic outline updates trigger refetches
  const outline = useVideoWorkspaceStore((state) => state.outline);

  // Track if there are any slides currently generating
  const hasGeneratingSlides = slideConversations.some(
    (sc) => sc.status === 'generating'
  );

  // Ref to track the last fetch time for debouncing
  const lastFetchRef = useRef<number>(0);

  // Fetch outline and extract slide statuses
  const fetchSlideStatuses = useCallback(async () => {
    if (!conversationId) return;

    // Debounce: don't fetch if less than 1 second since last fetch
    const now = Date.now();
    if (now - lastFetchRef.current < 1000) {
      return;
    }
    lastFetchRef.current = now;

    try {
      // API returns VideoOutline directly (or null if no outline)
      const fetchedOutline = await outlineAPI.get(conversationId);

      if (!fetchedOutline || !fetchedOutline.slides) {
        return;
      }

      // Update outline in store if changed
      // Read current outline from getState() (not closure) to avoid stale comparisons
      const currentOutline = useVideoWorkspaceStore.getState().outline;
      const { outlineLocalEditTimestamp } = useVideoWorkspaceStore.getState();

      if (JSON.stringify(fetchedOutline) !== JSON.stringify(currentOutline)) {
        if (outlineLocalEditTimestamp && currentOutline) {
          // User has pending local edits - merge server fields without overwriting user changes
          const merged = mergeServerOutlineIntoLocal(currentOutline, fetchedOutline);
          setOutline(merged);
        } else {
          // No pending edits - safe to replace wholesale
          setOutline(fetchedOutline);
        }
      }

      // Extract slide statuses and MERGE with existing slideConversations
      // This preserves real-time thinking updates from SSE while updating status from API
      const existingConversations = useVideoWorkspaceStore.getState().slideConversations;

      const newSlideConversations: SlideConversation[] = fetchedOutline.slides
        .filter((slide: { slideConversationId?: string; generationStatus?: string }) =>
          slide.slideConversationId || slide.generationStatus
        )
        .map((slide: {
          id: string;
          title: string;
          slideConversationId?: string;
          generationStatus?: string;
          generatingThinking?: string;
          generationError?: string;
        }) => {
          // Find existing conversation to preserve SSE-updated data
          const existing = existingConversations.find(sc => sc.outlineSlideId === slide.id);

          // Preserve SSE-updated status if it's 'completed' but outline still shows 'generating'
          // This handles the race condition where SSE completes before outline is refetched
          const effectiveStatus = (existing?.status === 'completed' && slide.generationStatus === 'generating')
            ? 'completed'
            : (slide.generationStatus as SlideGenerationStatus) || 'pending';

          // Determine thinking to show:
          // 1. If completed, no thinking
          // 2. If existing has SSE-updated thinking, use it
          // 3. Fall back to outline's persisted thinking (survives refresh)
          // 4. Default to "Processing..." if generating but no thinking yet
          let generatingThinking: string | undefined;
          if (effectiveStatus === 'completed') {
            generatingThinking = undefined;
          } else if (effectiveStatus === 'generating') {
            generatingThinking = existing?.generatingThinking || slide.generatingThinking || 'Processing...';
          }

          return {
            id: slide.slideConversationId || '',
            outlineSlideId: slide.id,
            title: slide.title,
            status: effectiveStatus,
            generatingThinking,
            errorMessage: slide.generationError,
          };
        });

      // Update store
      if (newSlideConversations.length > 0) {
        setSlideConversations(newSlideConversations);
      }
    } catch (error) {
      console.error('Failed to fetch slide statuses:', error);
    }
  }, [conversationId, setOutline, setSlideConversations]);

  // Poll for updates when there are generating slides
  useEffect(() => {
    if (!enabled || !conversationId) return;

    // Always do an initial fetch
    fetchSlideStatuses();

    // Only poll if there are generating slides
    if (!hasGeneratingSlides) {
      return;
    }

    const intervalId = setInterval(fetchSlideStatuses, pollInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    conversationId,
    enabled,
    pollInterval,
    hasGeneratingSlides,
    fetchSlideStatuses,
  ]);

  // Get status for a specific slide
  const getSlideStatus = useCallback(
    (outlineSlideId: string): SlideStatusInfo | undefined => {
      const slideConv = slideConversations.find(
        (sc) => sc.outlineSlideId === outlineSlideId
      );

      if (slideConv) {
        return {
          outlineSlideId,
          status: slideConv.status,
          slideConversationId: slideConv.id,
          generatingThinking: slideConv.generatingThinking,
          errorMessage: slideConv.errorMessage,
        };
      }

      // Check outline for status if not in slideConversations
      const outlineSlide = outline?.slides.find((s) => s.id === outlineSlideId);
      if (outlineSlide?.generationStatus) {
        return {
          outlineSlideId,
          status: outlineSlide.generationStatus as SlideGenerationStatus,
          slideConversationId: outlineSlide.slideConversationId,
          generatingThinking: outlineSlide.generatingThinking,
          errorMessage: outlineSlide.generationError,
        };
      }

      return undefined;
    },
    [slideConversations, outline]
  );

  // Check if all slides are completed
  const allSlidesCompleted = outline?.slides.every(
    (slide) => slide.generationStatus === 'completed'
  );

  // Check if any slides are generating
  const anySlidesGenerating = outline?.slides.some(
    (slide) => slide.generationStatus === 'generating'
  );

  // Check if any slides failed
  const anySlidesFailed = outline?.slides.some(
    (slide) => slide.generationStatus === 'failed'
  );

  // Get count of completed slides
  const completedCount =
    outline?.slides.filter((s) => s.generationStatus === 'completed').length || 0;

  const totalCount = outline?.slides.length || 0;

  return {
    slideConversations,
    getSlideStatus,
    refetch: fetchSlideStatuses,
    allSlidesCompleted,
    anySlidesGenerating,
    anySlidesFailed,
    completedCount,
    totalCount,
    progress: totalCount > 0 ? (completedCount / totalCount) * 100 : 0,
  };
}

export default useOutlineSlideStatus;
